import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  engineeringExecutionBibleApi,
  type AIDevRule,
  type APIStandard,
  type CICDRequirement,
  type CodingStandard,
  type DatabaseRule,
  type DefinitionOfDoneItem,
  type DeveloperPlaybookEntry,
  type EEBDashboard,
  type EEBibleStatus,
  type EngineeringPrinciple,
  type FeatureCompletionValidation,
  type FrontendStandard,
  type ObservabilityRequirement,
  type PerformanceBudget,
  type QualityGate,
  type RepositoryStructureRule,
  type ReviewChecklistItem,
  type SecurityRequirement,
  type TestingLevel,
} from '../../api/engineering-execution-bible';

type Tab = 'overview' | 'principles' | 'coding' | 'quality' | 'platform' | 'ops' | 'delivery' | 'status';
type Theme = 'light' | 'dark';

export function EngineeringExecutionBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EEBDashboard | null>(null);
  const [principles, setPrinciples] = useState<EngineeringPrinciple[]>([]);
  const [codingStandards, setCodingStandards] = useState<CodingStandard[]>([]);
  const [aiRules, setAiRules] = useState<AIDevRule[]>([]);
  const [qualityGates, setQualityGates] = useState<QualityGate[]>([]);
  const [performanceBudgets, setPerformanceBudgets] = useState<PerformanceBudget[]>([]);
  const [dbRules, setDbRules] = useState<DatabaseRule[]>([]);
  const [apiStandards, setApiStandards] = useState<APIStandard[]>([]);
  const [frontendStandards, setFrontendStandards] = useState<FrontendStandard[]>([]);
  const [observability, setObservability] = useState<ObservabilityRequirement[]>([]);
  const [security, setSecurity] = useState<SecurityRequirement[]>([]);
  const [testingLevels, setTestingLevels] = useState<TestingLevel[]>([]);
  const [dodItems, setDodItems] = useState<DefinitionOfDoneItem[]>([]);
  const [repoStructure, setRepoStructure] = useState<RepositoryStructureRule[]>([]);
  const [cicdReqs, setCicdReqs] = useState<CICDRequirement[]>([]);
  const [reviewChecklist, setReviewChecklist] = useState<ReviewChecklistItem[]>([]);
  const [playbook, setPlaybook] = useState<DeveloperPlaybookEntry[]>([]);
  const [status, setStatus] = useState<EEBibleStatus | null>(null);
  const [featResult, setFeatResult] = useState<FeatureCompletionValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [
        dash, pr, cs, ai, qg, pb, db, api, fe, obs, sec, ts, dod, repo, cicd, rc, pbk, st,
      ] = await Promise.all([
        engineeringExecutionBibleApi.dashboard(),
        engineeringExecutionBibleApi.principles(),
        engineeringExecutionBibleApi.codingStandards(),
        engineeringExecutionBibleApi.aiDevelopment(),
        engineeringExecutionBibleApi.qualityGates(),
        engineeringExecutionBibleApi.performanceBudgets(),
        engineeringExecutionBibleApi.databaseStandards(),
        engineeringExecutionBibleApi.apiStandards(),
        engineeringExecutionBibleApi.frontendStandards(),
        engineeringExecutionBibleApi.observability(),
        engineeringExecutionBibleApi.securityStandards(),
        engineeringExecutionBibleApi.testingStrategy(),
        engineeringExecutionBibleApi.definitionOfDone(),
        engineeringExecutionBibleApi.repositoryStructure(),
        engineeringExecutionBibleApi.cicdRequirements(),
        engineeringExecutionBibleApi.reviewChecklist(),
        engineeringExecutionBibleApi.developerPlaybook(),
        engineeringExecutionBibleApi.status(),
      ]);
      setDashboard(dash);
      setPrinciples(pr);
      setCodingStandards(cs);
      setAiRules(ai);
      setQualityGates(qg);
      setPerformanceBudgets(pb);
      setDbRules(db);
      setApiStandards(api);
      setFrontendStandards(fe);
      setObservability(obs);
      setSecurity(sec);
      setTestingLevels(ts);
      setDodItems(dod);
      setRepoStructure(repo);
      setCicdReqs(cicd);
      setReviewChecklist(rc);
      setPlaybook(pbk);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load engineering execution bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateFeature = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await engineeringExecutionBibleApi.validateFeature({
        feature_key: 'eeb-sample-feature',
        attrs: {
          production_code: true,
          automated_tests: true,
          documentation: true,
          observability: true,
          security_validated: true,
          performance_validated: true,
        },
      });
      setFeatResult(result);
      setActionMsg(result.valid ? 'Feature passes EEB-000001 definition of done' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-zinc-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900';
  const accent = dark ? 'text-zinc-300' : 'text-zinc-700';
  const tabActive = dark ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'principles', label: 'Principles' },
    { id: 'coding', label: 'Coding & AI' },
    { id: 'quality', label: 'Quality & Performance' },
    { id: 'platform', label: 'Platform Standards' },
    { id: 'ops', label: 'Observability & Security' },
    { id: 'delivery', label: 'Delivery & Playbook' },
    { id: 'status', label: 'Constitution Status' },
  ];

  const metricCards = dashboard ? [
    { label: 'Principles', value: dashboard.engineering_principles },
    { label: 'Coding Standards', value: dashboard.coding_standards },
    { label: 'Quality Gates', value: dashboard.quality_gates },
    { label: 'Performance Budgets', value: dashboard.performance_budgets },
    { label: 'Security Requirements', value: dashboard.security_requirements },
    { label: 'Testing Levels', value: dashboard.testing_levels },
    { label: 'DoD Items', value: dashboard.definition_of_done_items },
    { label: 'CI/CD Requirements', value: dashboard.cicd_requirements },
  ] : [];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>EEB-000001</p>
          <h1 className="text-2xl font-semibold">Engineering Execution Bible</h1>
          <p className={`text-sm ${muted}`}>
            Production development standards, coding constitution, AI-assisted engineering, quality gates, performance budgets and technical excellence
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/product-family-constitution" className={`${accent} hover:underline`}>P00060 Constitution</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-governance" className={`${accent} hover:underline`}>Engineering Governance</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Engineering Constitution</h2>
            <p className={`mt-1 text-sm ${muted}`}>
              Version {dashboard.version} · Applies to {dashboard.applies_to.join(', ')}
            </p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every line of code SHALL be production ready, maintainable, scalable, testable, observable, secure, AI-assisted, and future proof.
              No code bypasses this constitution. Engineering decisions favor 10-year maintainability over short-term speed.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metricCards.map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'principles' && (
        <div className="grid gap-3 md:grid-cols-2">
          {principles.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold">{p.name}</h3>
                {p.priority === 'first' && (
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">First</span>
                )}
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{p.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'coding' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Coding Standards</h2>
            <div className="space-y-3">
              {codingStandards.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{s.domain}</span>
                    <h3 className="font-semibold">{s.name}</h3>
                    {s.mandatory && <span className="text-xs text-emerald-600 dark:text-emerald-400">Mandatory</span>}
                  </div>
                  <ul className={`mt-2 list-inside list-disc text-sm ${muted}`}>
                    {s.rules.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">AI-Assisted Development</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {aiRules.map((r) => (
                <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{r.name}</h3>
                  <p className={`mt-2 text-sm ${muted}`}>{r.description}</p>
                  {r.required && <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Required</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'quality' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Quality Gates</h2>
            <div className="overflow-x-auto rounded-xl border">
              <table className={`min-w-full text-sm ${card}`}>
                <thead>
                  <tr className={`border-b text-left ${dark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                    <th className="px-4 py-2">Gate</th>
                    <th className="px-4 py-2">Tool</th>
                    <th className="px-4 py-2">Threshold</th>
                    <th className="px-4 py-2">Blocking</th>
                  </tr>
                </thead>
                <tbody>
                  {qualityGates.map((g) => (
                    <tr key={g.key} className={`border-b ${dark ? 'border-zinc-800' : 'border-zinc-100'}`}>
                      <td className="px-4 py-2 font-medium">{g.name}</td>
                      <td className={`px-4 py-2 ${muted}`}>{g.tool ?? '—'}</td>
                      <td className={`px-4 py-2 ${muted}`}>{g.threshold ?? '—'}</td>
                      <td className="px-4 py-2">{g.blocking ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Budgets</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {performanceBudgets.map((b) => (
                <div key={b.key} className={`rounded-xl border p-4 ${card}`}>
                  <p className={`text-xs uppercase ${muted}`}>{b.surface}</p>
                  <p className="mt-1 text-xl font-semibold">{b.max_value} {b.unit}</p>
                  {b.description && <p className={`mt-1 text-sm ${muted}`}>{b.description}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'platform' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Standards</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {dbRules.map((r) => (
                <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{r.name}</h3>
                  <p className={`mt-2 text-sm ${muted}`}>{r.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">API Standards</h2>
            <div className="space-y-3">
              {apiStandards.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{s.name}</h3>
                  <p className={`mt-2 text-sm ${muted}`}>{s.description}</p>
                  {s.example && <code className="mt-2 block rounded bg-zinc-100 px-2 py-1 text-xs dark:bg-zinc-800">{s.example}</code>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Frontend Standards</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {frontendStandards.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{s.category}</span>
                  <h3 className="mt-2 font-semibold">{s.name}</h3>
                  <p className={`mt-1 text-sm ${muted}`}>{s.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'ops' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Observability</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {observability.map((o) => (
                <div key={o.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{o.signal_type}</span>
                  <h3 className="mt-2 font-semibold">{o.name}</h3>
                  {o.mandatory && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Mandatory</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Security Requirements</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {security.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{s.name}</h3>
                  {s.standard && <p className={`mt-1 text-xs ${muted}`}>{s.standard}</p>}
                  {s.mandatory && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Mandatory</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing Strategy</h2>
            <div className="space-y-3">
              {testingLevels.map((l) => (
                <div key={l.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{l.name}</h3>
                  <p className={`text-sm ${muted}`}>{l.scope}</p>
                  {l.coverage && <p className={`mt-1 text-xs ${muted}`}>Coverage: {l.coverage}</p>}
                  {l.tools && l.tools.length > 0 && (
                    <p className={`mt-1 text-xs ${muted}`}>Tools: {l.tools.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'delivery' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Definition of Done</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {dodItems.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{d.category}</span>
                  <h3 className="mt-2 font-semibold">{d.name}</h3>
                  {d.required && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Required</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Repository Structure</h2>
            <div className="space-y-2">
              {repoStructure.map((r) => (
                <div key={r.key} className={`flex items-start gap-3 rounded-xl border p-3 ${card}`}>
                  <code className="shrink-0 rounded bg-zinc-100 px-2 py-1 text-xs dark:bg-zinc-800">{r.path}</code>
                  <p className={`text-sm ${muted}`}>{r.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">CI/CD Requirements</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {cicdReqs.map((c) => (
                <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{c.stage}</span>
                  <h3 className="mt-2 font-semibold">{c.name}</h3>
                  <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>
                  {c.blocking && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Blocking</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Review Checklist</h2>
            <ul className={`space-y-2 text-sm ${muted}`}>
              {reviewChecklist.map((c) => (
                <li key={c.key} className={`rounded-lg border px-3 py-2 ${card}`}>
                  <span className="text-xs uppercase">{c.category}</span> — {c.item}
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Developer Playbook</h2>
            <div className="space-y-4">
              {playbook.map((p) => (
                <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs dark:bg-zinc-700">{p.phase}</span>
                  <h3 className="mt-2 font-semibold">{p.title}</h3>
                  <ol className={`mt-2 list-inside list-decimal text-sm ${muted}`}>
                    {p.steps.map((s) => <li key={s}>{s}</li>)}
                  </ol>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                {status.status}
              </span>
              {status.engineering_constitution && (
                <span className={`text-sm ${muted}`}>Engineering Constitution Active</span>
              )}
              {status.ten_year_vision && (
                <span className={`text-sm ${muted}`}>10-Year Vision Enforced</span>
              )}
            </div>
            <p className={`mt-3 text-sm ${muted}`}>
              Bible {status.bible_id} v{status.version} · {status.applies_to.join(' · ')}
            </p>
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Feature (Definition of Done)</h3>
            <p className={`mt-1 text-sm ${muted}`}>
              Validates a feature against EEB §13 — production code, tests, documentation, observability, security, and performance.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={validateFeature}
              className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}
            >
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {actionMsg && <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">{actionMsg}</p>}
            {featResult && (
              <pre className={`mt-3 overflow-x-auto rounded-lg bg-zinc-100 p-3 text-xs dark:bg-zinc-800`}>
                {JSON.stringify(featResult, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
